import {
  ArrowUpRightFromSquare,
  ChevronDown,
  ChevronRight,
} from "@gravity-ui/icons";
import { Flex, Icon, Link, Text } from "@gravity-ui/uikit";
import { useState } from "react";
import { StoreBadge } from "src/components";

import { formatDeliveryRange, StoreOffersGroup } from "../../../../utils";
import { PriceItemDto } from "../../../../VariantTreeDto";
import { useProductOffersTranslate } from "./hooks";
import css from "./ProductOffers.module.scss";

interface ProductOffersProps {
  groups: StoreOffersGroup[];
  isSingleStore: boolean;
}

export const ProductOffers = ({
  groups,
  isSingleStore,
}: ProductOffersProps) => {
  if (!groups.length) return null;

  return (
    <Flex direction="column" gap="1">
      {groups.map((group) => (
        <ProductOffersStore
          key={`${group.store}/${isSingleStore}`}
          group={group}
          defaultOpen={isSingleStore}
        />
      ))}
    </Flex>
  );
};

const ProductOffersStore = ({
  group,
  defaultOpen,
}: {
  group: StoreOffersGroup;
  defaultOpen: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const translations = useProductOffersTranslate();

  return (
    <Flex direction="column" className={css.store}>
      <button
        type="button"
        className={css.storeHeader}
        onClick={() => setIsOpen((open) => !open)}
      >
        <Icon data={isOpen ? ChevronDown : ChevronRight} size={16} />

        <StoreBadge store={group.store} size={20} />

        <Text variant="subheader-2" ellipsis className={css.storeName}>
          {group.store}
        </Text>

        <Text variant="subheader-2">
          {group.minPrice} {group.currency}
        </Text>

        <Text variant="body-2" color="secondary" className={css.storeCount}>
          {translations.offers_count(group.offers.length)}
        </Text>
      </button>

      {isOpen && (
        <Flex direction="column" className={css.offerList}>
          {group.offers.map((offer, index) => (
            <ProductOfferRow key={index} offer={offer} />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

const ProductOfferRow = ({ offer }: { offer: PriceItemDto }) => {
  const translations = useProductOffersTranslate();

  const deliveryText = formatDeliveryRange(offer.delivery, {
    day: translations.delivery_days,
    hour: translations.delivery_hours,
  });

  return (
    <Flex className={css.offerRow} gap="2" alignItems="center">
      <Text
        variant="body-2"
        color="secondary"
        ellipsis
        className={css.offerSeller}
      >
        {offer.supplierName ?? translations.unknown_seller}
      </Text>

      <Text variant="body-2">
        {offer.price} {offer.currency}
      </Text>

      <Text variant="body-2" color="secondary" className={css.offerDelivery}>
        {deliveryText}
      </Text>

      {offer.url && (
        <Link
          href={offer.url}
          target="_blank"
          rel="noreferrer"
          title={translations.open_offer}
        >
          <Icon data={ArrowUpRightFromSquare} size={14} />
        </Link>
      )}
    </Flex>
  );
};
